// ProfitOfThePeriod.js
import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale } from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parseISO } from 'date-fns';

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);

// Dummy data
const allData = {
  monthly: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    data: [1000, 1200, 1100, 1300, 1400, 1200, 1600, 1700, 1500, 1800, 1700, 1900],
  },
  weekly: {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    data: [1200, 1500, 1800, 2200],
  },
  yearly: {
    labels: ['2020', '2021', '2022', '2023'],
    data: [15000, 16000, 17000, 18000],
  },
  custom: {
    labels: [], // Will be updated dynamically
    data: [],   // Will be updated dynamically
  },
};

function ProfitOfThePeriod() {
  const [range, setRange] = useState('monthly');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [customData, setCustomData] = useState({ labels: [], data: [] });

  useEffect(() => {
    if (range === 'custom') {
      // Simulate custom data fetching/filtering based on startDate and endDate
      const filteredData = {
        labels: ['Custom 1', 'Custom 2', 'Custom 3'], // Replace with actual date range labels
        data: [1200, 1500, 1700], // Replace with actual data based on date range
      };
      setCustomData(filteredData);
    }
  }, [startDate, endDate, range]);

  const handleRangeChange = (selectedRange) => {
    setRange(selectedRange);
  };

  const data = {
    labels: range === 'custom' ? customData.labels : allData[range].labels,
    datasets: [
      {
        label: 'Profit',
        data: range === 'custom' ? customData.data : allData[range].data,
        fill: false,
        backgroundColor: '#4CAF50',
        borderColor: '#4CAF50',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return `$${tooltipItem.formattedValue}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f3f3f3',
        },
      },
    },
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg h-full">
      <h3 className="text-xl font-semibold mb-4">Profit of the Period</h3>
      <div className="flex mb-4">
        <button
          className={`px-4 py-2 rounded-lg ${range === 'monthly' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => handleRangeChange('monthly')}
        >
          Monthly
        </button>
        <button
          className={`px-4 py-2 rounded-lg ml-2 ${range === 'weekly' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => handleRangeChange('weekly')}
        >
          Weekly
        </button>
        <button
          className={`px-4 py-2 rounded-lg ml-2 ${range === 'yearly' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => handleRangeChange('yearly')}
        >
          Yearly
        </button>
        <button
          className={`px-4 py-2 rounded-lg ml-2 ${range === 'custom' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => handleRangeChange('custom')}
        >
          Custom
        </button>
      </div>
      {range === 'custom' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Start Date</label>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
          <label className="block text-sm font-medium text-gray-700 mt-4">End Date</label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            dateFormat="yyyy-MM-dd"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      )}
      <div className="h-64">
        <Line data={data} options={options} />
      </div>
    </div>
  );
}

export default ProfitOfThePeriod;
